import React, { useRef, useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '../../molecules/modal/modal';
import Button, { ButtonTheme } from '../../atoms/button/button';
import UploadDragAndDrop from '../../molecules/uploadDragAndDrop/uploadDragAndDrop';
import FormInputText from '../../molecules/formInputText/formInputText';
import FormCheckbox from '../../molecules/formCheckbox/formCheckbox';
import FormSelect from '../../molecules/formSelect/formSelect';
import FormValidation from '../../atoms/formEnums/formValidation';
import Imagen from '../../atoms/imagen/imagen';
import { mapDispatchToProps } from '../../../store/externalCalls/api/uploadFilesApi';
import './externalResourceModal.scss';
import FormInputTypes from '../../atoms/formEnums/formInputTypes';
import { storeStructure } from '../../../store/reducers';
import { errorMessage } from '../../../utils/errors';
import { httpGet } from '../../../store/externalCalls/httpCalls';

const ExternalResource = (props) => {
  const {
    type,
    title,
    onClose,
    saveItemDownloadable,
    saveItemBackground,
    currentData,
    resetForm,
    id,
    aspectRatio,
    fileType,
    allowedSizes,
    imageHeight,
    imageWidth,
    onResolutionCheck,
  } = props;

  const childRef = useRef();
  const [showAlertResolution, setShowAlertResolution] = useState(false);

  const errorState = useSelector((state) => state[storeStructure.Errors]);
  const [disableForm, setDisableForm] = useState(currentData === {});
  const [data, setData] = useState(currentData);

  const handleSave = () => {
    switch (type) {
      case FormInputTypes.DOWNLOADABLE:
        saveItemDownloadable(data).then((downloadableResource) => {
          onClose(downloadableResource);
        }, (error) => errorMessage(error));
        break;
      case FormInputTypes.BACKGROUND:
        saveItemBackground(data).then((downloadableResource) => {
          onClose(downloadableResource);
        }, (error) => errorMessage(error));
        break;
      default:
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleOnChangeURL = (event) => {
    setData({ ...data, url: event.target.value });
  };

  const handlerOnChangeInput = (event, attributeName, useChecked = false) => {
    if (data !== {}) {
      setData({
        ...data,
        [attributeName]: useChecked ? event.target.checked : event.target.value,
      });
    } else {
      setData({
        [attributeName]: useChecked ? event.target.checked : event.target.value,
      });
    }
  };

  const handleUploadCompleted = (result) => {
    const { uploadURL } = result.successful[0].response;
    const publicFolder = uploadURL.substr(uploadURL.lastIndexOf('/'));
    const extension = result.successful[0].extension;
    const publicFileUrl = `${process.env.REACT_APP_BACKEND_URL + process.env.REACT_APP_PUBLIC_UPLOADS + publicFolder + publicFolder}.${extension}`;
    setData({ ...data, url: publicFileUrl });
    setDisableForm(false);
  };

  useEffect(() => {
    resetForm();
  }, [resetForm]);
 const checkResolution = async (url) => {
   setShowAlertResolution(false);

   if (onResolutionCheck) onResolutionCheck(false);
   return false;
 };


  return (
    <Modal ref={childRef} onClose={handleClose} title={title}>
      <div>
        {!disableForm && (
          <div className="text-center">
            <Imagen
              className="external-resource__image"
              src={data.url}
              alt={`Preview ${title}`}
            />
          </div>
        )}
        <UploadDragAndDrop
          aspectRatio={aspectRatio}
          onCompleted={handleUploadCompleted}
          fileType={fileType}
          allowedSizes={allowedSizes}
          imageHeight={imageHeight}
          imageWidth={imageWidth}
        />
        <FormInputText
          type={FormInputTypes.TEXT_INPUT.URL}
          helpText="Resource URL"
          label="URL"
          name="url"
          id={id}
          onChange={handleOnChangeURL}
          value={data.url}
          validation={
            errorState.url === undefined
              ? FormValidation.PENDING
              : FormValidation.ERROR
          }
          errorText={errorState.url === undefined ? "" : errorState.url[0]}
        />
        {type === FormInputTypes.BACKGROUND && [
          <FormSelect
            id={id}
            key="geometryID"
            type={FormInputTypes.SELECT}
            helpText="Geometry used for this background"
            label="Geometry"
            name="geometryID"
            options={[
              { name: "globe", value: 1 },
              { name: "cm32", value: 2 },
              { name: "dome180", value: 6 },
            ]}
            onChange={(event) => handlerOnChangeInput(event, "geometryID")}
            value={
              data.geometryID === undefined ? "1" : data.geometryID.toString()
            }
            validation={
              errorState.geometryID === undefined
                ? FormValidation.PENDING
                : FormValidation.ERROR
            }
            errorText={
              errorState.geometryID === undefined
                ? ""
                : errorState.geometryID[0]
            }
          />,
          <FormCheckbox
            id={id}
            key="isStereo"
            label="Is Stereo"
            helpText="Whether the background image is stereo or not "
            name="isStereo"
            onChange={(event) => handlerOnChangeInput(event, "isStereo", true)}
            checked={data.isStereo === undefined ? false : data.isStereo}
          />,
          <FormInputText
            id={id}
            key="rotation"
            type={FormInputTypes.TEXT_INPUT.TEXT}
            label="Rotation"
            helpText="Rotation around the Y axis that needs to be applied to the background image"
            name="rotation"
            onChange={(event) => handlerOnChangeInput(event, "rotation")}
            value={data.rotation === undefined ? "" : data.rotation.toString()}
            defaultValue="0"
            validation={
              errorState.rotation === undefined
                ? FormValidation.PENDING
                : FormValidation.ERROR
            }
            errorText={
              errorState.rotation === undefined ? "" : errorState.rotation[0]
            }
            pattern={/^[0-9\b]+$/}
          />,
        ]}
      </div>
      <div>
        <Button
          id={`button-${id}`}
          onClick={async () => {
            const showAlert = await checkResolution(data.url);

            handleSave();
            onClose({ ...data, showAlertResolution: showAlert });
          }}
          disabled={disableForm}
          theme={ButtonTheme.PRIMARY}
        >
          Save and Upload
        </Button>
      </div>
    </Modal>
  );
};

ExternalResource.propTypes = {
  /**
   * formtype
   */
  type: PropTypes.oneOf(Object.values(FormInputTypes)).isRequired,
  /**
   * Modal title
   */
  title: PropTypes.string,
  /**
   * Event callback when the modal is closed
   */
  onClose: PropTypes.func,
  /**
   * Function to save form values
   */
  saveItemDownloadable: PropTypes.func.isRequired,
  /**
   * Function to save form values
   */
  saveItemBackground: PropTypes.func.isRequired,
  /**
   * initial data values
   */
  currentData: PropTypes.shape({}),
  /**
   * Image aspect ratio
   */
  aspectRatio: PropTypes.string,
};

ExternalResource.defaultProps = {
  title: '',
  onClose: () => {},
  currentData: {},
  aspectRatio: '',
};

export default connect(null, mapDispatchToProps)(ExternalResource);
